import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { adminListEnquiries } from "@/lib/api/portal.functions";

export const Route = createFileRoute("/_authenticated/portal/admin/enquiries")({
  component: AdminEnquiries,
});

function AdminEnquiries() {
  const fetchAll = useServerFn(adminListEnquiries);
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-enquiries"],
    queryFn: () => fetchAll(),
  });
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const list: any[] = data?.messages ?? [];
    if (!q.trim()) return list;
    const needle = q.toLowerCase();
    return list.filter((m) =>
      [m.full_name, m.email, m.phone, m.service, m.message]
        .filter(Boolean)
        .some((v: string) => String(v).toLowerCase().includes(needle)),
    );
  }, [data, q]);

  if (error) return <p className="text-sm text-red-600">Forbidden or load failed.</p>;

  return (
    <section>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-display text-xl text-navy">All enquiries</h2>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, message…"
            className="w-64 rounded-md border border-input bg-background pl-8 pr-3 py-2 text-sm focus:border-gold focus:outline-none"
          />
        </div>
      </div>
      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      <p className="mb-2 text-xs text-muted-foreground">
        {filtered.length} of {data?.messages.length ?? 0} enquiries
      </p>
      <div className="space-y-3">
        {filtered.map((m: any) => (
          <div key={m.id} className="rounded-lg border border-border bg-card p-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="font-medium text-navy">{m.full_name}</p>
                <p className="text-xs text-muted-foreground">
                  {m.email} {m.phone ? `· ${m.phone}` : ""} {m.service ? `· ${m.service}` : ""}
                </p>
              </div>
              <span className="text-xs text-muted-foreground">
                {new Date(m.created_at).toLocaleString()}
              </span>
            </div>
            <p className="mt-2 whitespace-pre-wrap text-sm text-foreground/80">{m.message}</p>
          </div>
        ))}
        {data && filtered.length === 0 && (
          <p className="text-sm text-muted-foreground">No enquiries match your search.</p>
        )}
      </div>
    </section>
  );
}
